import React from 'react';
import { 
  ProjectPlanningCategory, 
  ProjectTypeDefinition,
  ProjectTypeRecommendation
} from '../../types/project-types';
import { Project } from '../../types';

interface TypeSelectionStepProps {
  recommendations: ProjectTypeRecommendation[];
  availableTypes: ProjectTypeDefinition[];
  selectedType?: ProjectPlanningCategory;
  onSelect: (category: ProjectPlanningCategory) => void;
  project?: Partial<Project>;
}

export const TypeSelectionStep: React.FC<TypeSelectionStepProps> = ({
  recommendations,
  availableTypes,
  selectedType,
  onSelect,
  project
}) => {
  // Sort types: recommended first, then others
  const sortedTypes = [...availableTypes].sort((a, b) => {
    const aRecommended = recommendations.find(r => r.category === a.category);
    const bRecommended = recommendations.find(r => r.category === b.category);
    
    if (aRecommended && !bRecommended) return -1;
    if (!aRecommended && bRecommended) return 1;
    if (aRecommended && bRecommended) return bRecommended.confidence - aRecommended.confidence;
    return 0;
  });

  const getTypeIcon = (category: ProjectPlanningCategory) => {
    const icons = {
      [ProjectPlanningCategory.LEARNING_GOAL]: '🎓',
      [ProjectPlanningCategory.PROJECT_GOAL]: '🎯',
      [ProjectPlanningCategory.PERSONAL_GOAL]: '💪',
      [ProjectPlanningCategory.VISION_GOAL]: '🔮',
      [ProjectPlanningCategory.TECHNICAL_GOAL]: '⚙️'
    };
    return icons[category] || '📋';
  };

  const getCharacteristicsDisplay = (definition: ProjectTypeDefinition) => {
    const { characteristics } = definition;
    return [
      `Trigger: ${characteristics.triggerType}`,
      `Horizont: ${characteristics.timeHorizon}`,
      `Messung: ${characteristics.measurementFocus}`
    ].join(' • ');
  };

  const getRecommendationBadge = (category: ProjectPlanningCategory) => {
    const recommendation = recommendations.find(r => r.category === category);
    if (!recommendation) return null;

    const confidenceColor = 
      recommendation.confidence >= 80 ? 'bg-green-100 text-green-800 border-green-200' :
      recommendation.confidence >= 60 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
      'bg-blue-100 text-blue-800 border-blue-200';

    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${confidenceColor}`}>
        ⭐ {recommendation.confidence}% empfohlen
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Welche Art von Projekt planst du?
        </h3>
        <p className="text-gray-600">
          Wähle die Kategorie, die am besten zu deinem Projektziel passt. 
          {recommendations.length > 0 && " Basierend auf deiner Beschreibung haben wir Empfehlungen markiert."}
        </p>
      </div>

      {/* Project Context */}
      {project?.description && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Dein Projekt:</h4>
          <p className="text-gray-700 text-sm">"{project.description}"</p>
        </div>
      )}

      {/* AI Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">🤖 AI-Empfehlungen:</h4>
          <div className="space-y-2">
            {recommendations.slice(0, 2).map((rec, index) => (
              <div key={rec.category} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{getTypeIcon(rec.category)}</span>
                  <div>
                    <span className="font-medium text-blue-900">
                      {availableTypes.find(t => t.category === rec.category)?.label}
                    </span>
                    <span className="text-blue-700 text-sm ml-2">
                      {rec.confidence}% Match
                    </span>
                  </div>
                </div>
                <div className="text-xs text-blue-600">
                  #{index + 1} Empfehlung
                </div>
              </div>
            ))}
          </div>
          {recommendations.length > 2 && (
            <p className="text-xs text-blue-600 mt-2">
              +{recommendations.length - 2} weitere Empfehlungen verfügbar
            </p>
          )}
        </div>
      )}

      {/* Type Selection Cards */}
      <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
        {sortedTypes.map((definition) => {
          const isSelected = selectedType === definition.category;
          const isRecommended = recommendations.some(r => r.category === definition.category);
          
          return (
            <div
              key={definition.category}
              className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                isSelected 
                  ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              } ${
                isRecommended ? 'ring-1 ring-yellow-300' : ''
              }`}
              onClick={() => onSelect(definition.category)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{getTypeIcon(definition.category)}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {definition.label}
                    </h4>
                  </div>
                </div>
                {getRecommendationBadge(definition.category)}
              </div>

              {/* Description */}
              <p className="text-gray-600 text-sm mb-3">
                {definition.description}
              </p>

              {/* Characteristics */}
              <div className="text-xs text-gray-500 mb-3">
                {getCharacteristicsDisplay(definition)}
              </div>

              {/* Planning Steps Preview */}
              <div className="space-y-2">
                <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                  Planungsschritte:
                </h5>
                <div className="space-y-1">
                  {definition.planningSteps.slice(0, 3).map((step, index) => (
                    <div key={step.order} className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-xs text-gray-600">{index + 1}</span>
                      </div>
                      <span className="text-xs text-gray-600">{step.name}</span>
                    </div>
                  ))}
                  {definition.planningSteps.length > 3 && (
                    <div className="text-xs text-gray-500 ml-6">
                      +{definition.planningSteps.length - 3} weitere Schritte
                    </div>
                  )}
                </div>
              </div>

              {/* Artifacts Preview */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">
                  Erzeugte Artefakte:
                </h5>
                <div className="flex flex-wrap gap-1">
                  {definition.artifactTypes.slice(0, 3).map((artifact) => (
                    <span 
                      key={artifact.name}
                      className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                    >
                      {artifact.name}
                    </span>
                  ))}
                  {definition.artifactTypes.length > 3 && (
                    <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded">
                      +{definition.artifactTypes.length - 3}
                    </span>
                  )}
                </div>
              </div>

              {/* Selection Indicator */}
              {isSelected && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="flex items-center text-blue-600 text-sm">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    Ausgewählt
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">💡 Nicht sicher?</h4>
        <p className="text-gray-600 text-sm">
          Du kannst den Projekttyp später noch ändern. Die AI-Empfehlungen basieren auf 
          Schlüsselwörtern in deiner Projektbeschreibung. Bei Unsicherheit wähle den 
          Typ mit der höchsten Empfehlung oder den allgemeinen "Projektziel"-Typ.
        </p>
      </div>
    </div>
  );
};